import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { id: "hero", label: "01 / Intro" },
  { id: "story", label: "02 / Story" },
  { id: "timeline", label: "03 / Timeline" },
  { id: "education", label: "04 / Education" },
  { id: "sports", label: "05 / Sports" },
  { id: "experience", label: "06 / Experience" },
  { id: "projects", label: "07 / Work" },
  { id: "moments", label: "08 / Moments" },
  { id: "awards", label: "09 / Awards" },
  { id: "credentials", label: "10 / Credentials" },
  { id: "skills", label: "11 / Skills" },
  { id: "geography", label: "12 / Journey" },
  { id: "vision", label: "13 / Vision" },
  { id: "contact", label: "14 / Contact" },
];

const CvNavbar = () => {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div
            className={`flex items-center justify-between rounded-full px-4 md:px-6 py-3 transition-all duration-500 ${
              scrolled
                ? "bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/40"
                : "bg-transparent"
            }`}
          >
            <button
              onClick={() => scrollTo("hero")}
              className="flex items-center gap-3 group"
              data-testid="nav-logo"
            >
              <div className="w-10 h-10 rounded-full grid place-items-center bg-black/40 border border-[#e6c769]/30 group-hover:border-[#e6c769]/70 transition-colors overflow-hidden">
                <img
                  src="/favicon.png"
                  alt="UB monogram"
                  className="w-7 h-7 object-contain"
                />
              </div>
              <div className="hidden md:block leading-tight text-left">
                <div className="text-[13px] font-medium text-white tracking-wide">Utkarsh Bokadia</div>
                <div className="text-[10px] text-white/50 tracking-[0.2em] uppercase">Personal Archive</div>
              </div>
            </button>

            <div className="flex items-center gap-3">
              <button
                onClick={() => scrollTo("contact")}
                className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#e6c769]/40 text-[#e6c769] hover:bg-[#e6c769] hover:text-black transition-all text-[12px] tracking-[0.18em] uppercase font-medium"
              >
                Get in touch
              </button>
              <button
                onClick={() => setOpen(true)}
                className="w-10 h-10 grid place-items-center rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors"
                aria-label="Open navigation"
                data-testid="nav-menu-open"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#06070b]/95 backdrop-blur-2xl"
          >
            <div className="max-w-2xl mx-auto px-6 py-8">
              <div className="flex items-center justify-between">
                <div className="text-[12px] tracking-[0.3em] uppercase text-white/50">Navigation</div>
                <button onClick={() => setOpen(false)} className="w-10 h-10 grid place-items-center rounded-full border border-white/10 text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <ul className="mt-12 space-y-4">
                {LINKS.map((l, i) => (
                  <motion.li
                    key={l.id}
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <button
                      onClick={() => scrollTo(l.id)}
                      className="w-full text-left text-3xl md:text-5xl font-serif text-white/80 hover:text-[#e6c769] transition-colors"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {l.label}
                    </button>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CvNavbar;
